/**
 * The sentence shown for each code the sign-in routes can send the browser back
 * with, as `/signin?error=<code>`.
 *
 * It is a module of its own so it can be tested. Two properties matter and
 * neither is visible by reading the table:
 *
 * - Every code the API can emit has a sentence here. A code with no entry falls
 *   through to "Sign-in failed.", which tells the person nothing and tells an
 *   operator nothing either. The Go guard beside the handlers
 *   (TestEverySignInErrorCodeHasSPACopy) reads the constants out of the source
 *   and compares them with this table, because a new code added on one side of
 *   the wire is exactly the drift a table maintained by hand suffers.
 *
 * - None of these sentences repeats an address, a domain or anything Google
 *   said. The sign-in page is reachable by anyone, and the codes arrive in a URL
 *   that anyone can construct: a message that echoed its input would turn this
 *   page into a way to put chosen text, or a chosen address, in front of
 *   somebody else.
 */
export const SIGN_IN_ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled.',
  invalid_flow: 'This sign-in attempt has expired or was started in another browser. Try again.',
  // Both callbacks emit the five codes above and below this line, so none of
  // those sentences may name Google: a person who came through their company's
  // identity provider and read "Google could not be reached" would go looking
  // in exactly the wrong place.
  provider_unavailable: 'Your sign-in provider could not be reached. Try again in a moment.',
  invalid_token: 'That sign-in could not be verified. Try again.',
  no_connection:
    'No company on this docsdone signs in with that email domain. Use a code instead, or ask an administrator.',
  needs_second_factor:
    'Your company requires a second factor. Sign in with a code instead.',
  not_bound:
    'Your company is not connected to this docsdone yet. An administrator has to connect it; sign in with a code in the meantime.',
  unverified_email:
    "Google reports this account's email address as unverified. Verify it with Google, then try again.",
  wrong_domain: "This docsdone is limited to accounts in its organisation's Google domain.",
  // The company sign-in callback emits this one too, so it names no provider.
  not_invited: 'This account has not been invited to docsdone. Ask an administrator to invite you.',
  bad_return_to:
    'That sign-in link asked to come back to somewhere this app does not go. Sign in from here instead.',
  internal:
    'Something went wrong on our side. Trying again will not help; tell an administrator what you saw here.',
}

/** The fallback for a code this build has never heard of. */
export const SIGN_IN_FALLBACK = 'Sign-in failed.'

export function signInErrorMessage(code: string | null): string | null {
  if (!code) return null
  // hasOwn, not a bare lookup. `SIGN_IN_ERRORS[code]` reaches Object.prototype,
  // so ?error=toString renders "function toString() { [native code] }" onto the
  // page instead of the fallback — attacker-chosen text on a page anyone can
  // reach, from a URL anyone can construct, which is the one thing this table
  // is written to avoid. React escapes it, so it is not script injection; it is
  // still a sentence docsdone did not write, shown as if it had.
  if (!Object.hasOwn(SIGN_IN_ERRORS, code)) return SIGN_IN_FALLBACK
  return SIGN_IN_ERRORS[code]
}
